import { useState } from "react"
import { useLocation } from "react-router-dom"
import toast from "react-hot-toast"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import { useRecipes } from "@/hooks/useRecipes"
import { useSoapCalculator } from "@/hooks/useSoapCalculator"
import { useMoldPresets } from "@/hooks/useMoldPresets"
import { DashboardHeader } from "@/components/DashboardHeader"
import { RecipeSettings } from "@/components/RecipeSettings"
import { OilSelection } from "@/components/OilSelection"
import { QualityMetrics } from "@/components/QualityMetrics"
import { CostBreakdown } from "@/components/CostBreakdown"
import { PrintableRecipeModal } from "@/components/PrintableRecipeModal"
import { SeoContent } from "@/components/SeoContent"
import { AuthModal } from "@/components/AuthModal"
import { SaveRecipeModal } from "@/components/SaveRecipeModal"
import { DEFAULT_PRICING_INPUTS, type PricingInputs } from "@/utils/pricing"

export default function Home() {
  const location = useLocation()
  const { user, isAuthenticated } = useAuth()
  const { saveRecipe, updateRecipe } = useRecipes()
  const {
    presets: moldPresets,
    loading: moldPresetsLoading,
    saving: moldPresetsSaving,
    savePreset: saveMoldPreset,
    deletePreset: deleteMoldPreset,
    canSave: canSaveMoldPresets,
  } = useMoldPresets(isAuthenticated)

  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [showPrintable, setShowPrintable] = useState(false)
  const [showAuth, setShowAuth] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [saveMode, setSaveMode] = useState<"save" | "update" | "saveAsNew">("save")
  const [pricing, setPricing] = useState<PricingInputs>(DEFAULT_PRICING_INPUTS)

  const {
    unit, setUnit,
    liquidType, setLiquidType,
    lyeType, setLyeType,
    superfat, setSuperfat,
    waterPercent, setWaterPercent,
    lyeCalculationMode, setLyeCalculationMode,
    lyeConcentration, setLyeConcentration,
    batchSize, setBatchSize,
    fragrancePercent, setFragrancePercent,
    oils, addOil, removeOil, updateOil, balanceToHundred,
    calculations,
    totalPercentage,
    loadedRecipeId,
    loadedRecipeName,
    setLoadedRecipe,
  } = useSoapCalculator(location.state?.loadRecipe)

  const openSaveModal = (mode: "save" | "update" | "saveAsNew") => {
    if (!isAuthenticated) {
      setShowAuth(true)
      return
    }
    setSaveMode(mode)
    setShowSaveModal(true)
  }

  const handleSaveClick = () => {
    openSaveModal(loadedRecipeId ? "update" : "save")
  }

  const handleSaveAsNewClick = () => {
    openSaveModal("saveAsNew")
  }

  const handleSaveRecipe = async (name: string, description: string, tags: string[], isFavorite: boolean) => {
    const recipePayload = {
      name,
      description,
      tags,
      isFavorite,
      unit,
      liquidType,
      lyeType,
      superfat,
      waterPercent,
      lyeCalculationMode,
      lyeConcentration,
      batchSize,
      fragrancePercent,
      oils: oils.map((o) => ({ oil: o.oil, percentage: o.percentage, costPerUnit: o.costPerUnit })),
    }

    try {
      if (saveMode === "update" && loadedRecipeId) {
        await updateRecipe(loadedRecipeId, recipePayload)
        setLoadedRecipe(loadedRecipeId, name)
      } else {
        const created = await saveRecipe(recipePayload) as { _id?: string } | undefined
        setLoadedRecipe(created?._id || null, name)
      }
      setShowSaveModal(false)
    } catch {
      toast.error("Failed to save recipe. Please try again.")
    }
  }

  const handleSignOut = async () => {
    await lumi.auth.signOut()
    toast.success("Signed out successfully")
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        isAuthenticated={isAuthenticated}
        user={user}
        loadedRecipeId={loadedRecipeId}
        loadedRecipeName={loadedRecipeName}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        onSaveClick={handleSaveClick}
        onSaveAsNewClick={handleSaveAsNewClick}
        onSignOut={handleSignOut}
        onShowAuth={() => setShowAuth(true)}
        onShowPrintable={() => setShowPrintable(true)}
      />

      {/* Dashboard Grid */}
      <main className="max-w-[1800px] mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <RecipeSettings
            unit={unit} setUnit={setUnit}
            liquidType={liquidType} setLiquidType={setLiquidType}
            lyeType={lyeType} setLyeType={setLyeType}
            superfat={superfat} setSuperfat={setSuperfat}
            waterPercent={waterPercent} setWaterPercent={setWaterPercent}
            lyeCalculationMode={lyeCalculationMode} setLyeCalculationMode={setLyeCalculationMode}
            lyeConcentration={lyeConcentration} setLyeConcentration={setLyeConcentration}
            batchSize={batchSize} setBatchSize={setBatchSize}
            fragrancePercent={fragrancePercent} setFragrancePercent={setFragrancePercent}
            calculations={calculations}
            moldPresets={moldPresets}
            moldPresetsLoading={moldPresetsLoading}
            moldPresetsSaving={moldPresetsSaving}
            canSaveMoldPresets={canSaveMoldPresets}
            onSaveMoldPreset={saveMoldPreset}
            onDeleteMoldPreset={deleteMoldPreset}
            onRequestSignIn={() => setShowAuth(true)}
          />

          <OilSelection
            oils={oils}
            batchSize={batchSize}
            unit={unit}
            totalPercentage={totalPercentage}
            addOil={addOil}
            removeOil={removeOil}
            updateOil={updateOil}
            balanceToHundred={balanceToHundred}
          />

          <aside className="lg:col-span-3 space-y-6">
            <QualityMetrics
              calculations={calculations}
              hasOils={oils.length > 0}
            />
            <CostBreakdown
              calculations={calculations}
              unit={unit}
              batchSize={batchSize}
              hasOils={oils.length > 0}
              pricing={pricing}
              onPricingChange={setPricing}
            />
          </aside>
        </div>
      </main>

      <SeoContent />

      {/* Modals */}
      {showPrintable && (
        <PrintableRecipeModal
          calculations={calculations}
          unit={unit}
          batchSize={batchSize}
          superfat={superfat}
          lyeType={lyeType}
          liquidType={liquidType}
          fragrancePercent={fragrancePercent}
          pricing={pricing}
          onClose={() => setShowPrintable(false)}
        />
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

      {showSaveModal && (
        <SaveRecipeModal
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveRecipe}
          initialName={loadedRecipeName}
          mode={saveMode}
        />
      )}
    </div>
  )
}
